// client/src/components/social/FederationFollowButton.js
import React, { useState, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import axiosInstance from '../../axiosInstance';

/**
 * Follow/Unfollow button for remote (federated) users.
 * Uses federation endpoints so an AP Undo{Follow} is sent on unfollow.
 *
 * Props:
 *   shadowUserId  — local shadow user _id (for status check)
 *   acct          — "username@domain" handle (for follow)
 *   actorUrl      — AP actor URL / federationId (for unfollow)
 *   size          — MUI button size, default 'small'
 */
export default function FederationFollowButton({ shadowUserId, acct, actorUrl, size = 'small' }) {
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!shadowUserId) { setLoading(false); return; }
    axiosInstance.get(`/follow/status/${shadowUserId}`)
      .then(res => {
        setFollowing(res.data.status === 'following' || res.data.status === 'mutual');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [shadowUserId]);

  const handleFollow = async () => {
    if (!acct) return;
    setActing(true);
    try {
      await axiosInstance.post('/fed/follow-remote', { acct });
      setFollowing(true);
    } catch (e) {
      if (e.response?.status === 400) setFollowing(true); // already following
    } finally {
      setActing(false);
    }
  };

  const handleUnfollow = async () => {
    if (!actorUrl) return;
    setActing(true);
    try {
      await axiosInstance.delete('/fed/follow-remote', { data: { actorUrl } });
      setFollowing(false);
    } catch {
      // ignore
    } finally {
      setActing(false);
    }
  };

  if (loading) return <CircularProgress size={16} />;

  return (
    <Button
      size={size}
      variant={following ? 'contained' : 'outlined'}
      color={following ? 'primary' : 'inherit'}
      startIcon={acting
        ? <CircularProgress size={14} color="inherit" />
        : (following ? <PersonRemoveIcon fontSize="small" /> : <PersonAddIcon fontSize="small" />)
      }
      onClick={following ? handleUnfollow : handleFollow}
      disabled={acting}
      fullWidth
    >
      {following ? 'Unfollow' : 'Follow'}
    </Button>
  );
}
